/* Copyright (c) 2020-present CloudIO Inc */

import { useEffect, useRef } from 'react';
import stableStringify from 'json-stable-stringify';
import { getErrorMessage } from 'utils/isEmpty';

function stringify(obj: unknown) {
  try {
    return stableStringify(obj, {
      replacer: (key, value) => {
        if (typeof value === 'function') {
          return 'fn';
        }
        if (value && typeof value === 'object' && value['$$typeof']) {
          return value['$$typeof'];
        }
        return value;
      },
    });
  } catch (e) {
    return getErrorMessage(e);
  }
}

/**
 * A hook that logs changes to React props that gets changed between renders.
 * @param name Component Name
 * @param props An object with the component props to be compared
 * @param printKeysOnly If true, only the keys that changed will be printed. If false, will print along with the values.
 */
export default function useWhyDidYouUpdate(
  name: string,
  props: Record<string, unknown>,
  printKeysOnly = false,
) {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef<Record<string, unknown>>(props);

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      // Use this object to keep track of changed props
      const changesObj: Record<string, unknown> = {};
      const changedKeys: string[] = [];
      // Iterate through keys
      allKeys.forEach((key) => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          if (printKeysOnly) {
            changedKeys.push(key);
          } else {
            changesObj[key] = {
              from: stringify(previousProps.current[key]),
              to: stringify(props[key]),
            };
          }
        }
      });

      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length || changedKeys.length) {
        // eslint-disable-next-line no-console
        console.info(
          `[why-did-you-update][${name}]`,
          printKeysOnly ? changedKeys : changesObj,
        );
      } else {
        // eslint-disable-next-line no-console
        console.info(`[why-did-you-update][${name}] no changes!`);
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props;
  }, [name, props, printKeysOnly]);
}
